/**
 * PlaceSearchOverlay — full-screen city search for Place of Birth.
 *
 * Rendered as a fixed full-screen layer over the form (not a route), so the
 * search input sits pinned at the top and the results fill the screen below —
 * the soft keyboard never covers them. Mounting/unmounting it with the open
 * state ties useGeoSearch's lifecycle to the overlay (fresh state each open).
 *
 * On a geo-API failure with a usable query, we still let the user commit the
 * text they typed (Android's fallback) so a flaky lookup never blocks submit.
 */
import { MIN_CHARS, useGeoSearch } from './useGeoSearch'
import { CloseIcon, LocationIcon, SearchIcon } from './icons'
import { BackArrowIcon } from '../icons'

interface Props {
  onSelect: (city: string) => void
  onClose: () => void
}

export function PlaceSearchOverlay({ onSelect, onClose }: Props) {
  const { query, setQuery, results, loading, failed } = useGeoSearch()
  const trimmed = query.trim()
  const tooShort = trimmed.length > 0 && trimmed.length < MIN_CHARS
  const canUseTyped = trimmed.length >= MIN_CHARS
  const noMatches = !loading && !failed && results.length === 0 && canUseTyped

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Search your city"
      className="askk-theme fixed inset-0 z-50 flex flex-col bg-surfaceZero pb-[env(safe-area-inset-bottom)]"
    >
      <div className="mx-auto flex w-full max-w-[600px] flex-1 flex-col overflow-hidden">
        {/* Header: back + search box */}
        <div className="flex items-center gap-2 px-2 py-3">
          <button
            type="button"
            onClick={onClose}
            aria-label="Back"
            className="flex h-10 w-10 items-center justify-center text-textPrimary"
          >
            <BackArrowIcon className="h-6 w-6" />
          </button>
          <div className="flex flex-1 items-center gap-2 rounded-full bg-surfaceOne px-4 py-2.5">
            <SearchIcon className="h-5 w-5 shrink-0 text-textSecondary" />
            <input
              type="text"
              autoFocus
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              aria-label="Search your city"
              placeholder="Search your city"
              className="flex-1 bg-transparent text-labelLarge text-textPrimary placeholder:text-textDisabled focus:outline-none"
            />
            {query && (
              <button
                type="button"
                onClick={() => setQuery('')}
                aria-label="Clear"
                className="shrink-0 text-textSecondary"
              >
                <CloseIcon className="h-4 w-4" />
              </button>
            )}
          </div>
        </div>

        {/* Results */}
        <div className="flex-1 overflow-y-auto px-4">
          {tooShort && (
            <p className="py-4 text-bodySmall text-textDisabled">Type at least 3 characters</p>
          )}

          {loading && !tooShort && (
            <p className="py-4 text-bodyMedium text-textSecondary">Searching…</p>
          )}

          {!loading &&
            results.map((city) => (
              <button
                key={city}
                type="button"
                onClick={() => onSelect(city)}
                className="flex w-full items-center gap-3 border-b border-textSeparator py-3.5 text-left"
              >
                <LocationIcon className="h-5 w-5 shrink-0 text-textSecondary" />
                <span className="text-labelLarge text-textPrimary">{city}</span>
              </button>
            ))}

          {/* Fallback: API failed or returned nothing, but the typed text is usable. */}
          {!loading && (failed || noMatches) && canUseTyped && (
            <button
              type="button"
              onClick={() => onSelect(trimmed)}
              className="flex w-full items-center gap-3 py-3.5 text-left"
            >
              <LocationIcon className="h-5 w-5 shrink-0 text-textSecondary" />
              <span className="text-labelLarge text-textPrimary">
                Use “{trimmed}”
              </span>
            </button>
          )}
        </div>
      </div>
    </div>
  )
}
